import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { Layer } from '@connect-services/client/sdk/api';
import {
    ContentLayoutService,
    PUZZLE_2_3_HOME_GRID_PARAMS,
} from '@connect-services/client/services/content-layout.service';
import { LayerEditService } from '@connect-services/client/services/layer-edit.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';

export class PanelAction {
    public disabledReason?: string[];
    public icon?: IconProp;
    public isActive!: boolean;
    public label!: string;
    public loading!: boolean;
    public onClick!: () => void | Promise<void>;
    public tooltipMessage: string;
    public type!: ButtonType;

    public constructor(values: {
        defaultDisabledTooltipMessage?: string;
        disabledReason?: string[];
        icon?: IconProp;
        isActive?: boolean;
        label: string;
        loading?: boolean;
        onClick: () => void | Promise<void>;
        type?: ButtonType;
    }) {
        this.disabledReason = values.disabledReason;
        this.icon = values.icon;
        this.isActive = values.isActive ?? true;
        this.label = values.label;
        this.loading = values.loading ?? false;
        this.onClick = values.onClick;
        this.type = values.type ?? ButtonType.PRIMARY;
        this.tooltipMessage = this.getTooltipMessage(values.defaultDisabledTooltipMessage);
    }

    private getTooltipMessage(defaultMessage: string = ''): string {
        if (this.isActive) {
            return '';
        }

        return this.disabledReason?.join('\n') ?? defaultMessage;
    }
}

@Injectable({
    providedIn: 'root',
})
export class DetailPanelService {
    public readonly actions = signal<PanelAction[]>([]);
    public readonly activeLayerType: WritableSignal<Layer.TypeEnum | null> = signal(null);
    public readonly expandedVertically: Signal<boolean> = computed(() => {
        return [
            PUZZLE_2_3_HOME_GRID_PARAMS.TWO_THIRDS_VERTICAL,
            PUZZLE_2_3_HOME_GRID_PARAMS.PUZZLE_TWO_THIRDS_ONE_THIRD,
            PUZZLE_2_3_HOME_GRID_PARAMS.PUZZLE_TWO_THIRDS_TWO_THIRDS,
        ].includes(this.contentLayoutService.mapSizeType());
    });
    public readonly navigateTo: WritableSignal<string[]> = signal([]);
    public readonly title: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

    protected detailPanelOutletRegex: RegExp = /\/\(detailPanel:([a-zA-Z0-9-]+)/;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly layerEditService = inject(LayerEditService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);

    public clear(): void {
        this.actions.set([]);
        this.title.set(undefined);
    }

    public getDetailPanelUuidFromUrl(): string | null {
        const currentUrl = this.router.url;
        const match = currentUrl.match(this.detailPanelOutletRegex);

        return match ? match[1] : null;
    }

    public getDetailPanelUuidFromUrlByParameter(routePathParameter: RoutePathEnum): string | null {
        const currentUrl = this.router.url;
        const detailPanelOutletRegex = new RegExp(`\\(detailPanel:${routePathParameter}\\/([a-zA-Z0-9-]+)\\)`);

        const match = currentUrl.match(detailPanelOutletRegex);

        return match ? match[1] : null;
    }

    public isOpen(): boolean {
        return this.getDetailPanelUuidFromUrl() !== null;
    }

    public async toggleLayerEntityDetailPanel(entityId: string): Promise<void> {
        const currentEntityId = this.getDetailPanelUuidFromUrl();
        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id, RoutePathEnum.SETTINGS];
        const panelParam = currentEntityId && currentEntityId === entityId ? null : entityId;
        this.layerEditService.isEditing.set(false);
        this.layerEditService.isEditingSymbology.set(false);

        await this.router.navigate(
            [
                ...path,
                {
                    outlets: { detailPanel: panelParam },
                },
            ],
            {
                queryParams: { ...this.activatedRoute.snapshot.queryParams },
                queryParamsHandling: 'merge',
            },
        );
    }
}
